"use client";

/**
 * Firestore data access layer — using TanStack Query.
 * Centralizes all reads/writes so security rules can evolve independently.
 */

import {
  collection, doc, getDoc, getDocs, query, where, orderBy,
  addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp,
  type DocumentData,
} from "firebase/firestore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFirebase } from "./client";

// ============ Types (subset of full schema in types.ts) ============

export interface CLO {
  id: string;
  text: string;
  bloomsLevel: string;
  bloomsVerb: string;
  ploMappings: string[];
  passThreshold: number;
  targetAchievementRate: number;
}

export interface CourseSection {
  section: number;          // 1, 2, 3...
  instructorUid: string;
  instructorName: string;
  semester?: string;        // "1/68" or "2/68"
  cancelled?: boolean;      // ห้องนี้ถูกยกเลิกเพราะไม่มีนิสิตลงทะเบียน — ข้ามใน TQF5 validation
  cancelledReason?: string; // เหตุผลที่ยกเลิก
  cancelledAt?: string;     // ISO date
  studentCount?: number;
}

export interface CourseLite {
  id: string;
  programId: string;
  code: string;
  nameTh: string;
  nameEn?: string;
  credit: number;
  creditString?: string;
  category?: string;
  description?: { th?: string; en?: string } | null;
  instructorUid: string;             // Primary instructor (coordinator / first section)
  instructorName: string;
  sections?: CourseSection[];        // All sections with their instructors
  allInstructorUids?: string[];      // Flat array for array-contains queries
  lastVerifiedYear?: number;
  verifiedYears?: number[];
  active: boolean;

  // From curriculum sync
  ploResponsibility?: string[];
  ploLevels?: Record<string, "I" | "R" | "M">;
  ploSubCodes?: Record<string, string[]>;
  kscMapping?: { k?: string[]; s?: string[]; e?: string[]; c?: string[] };
  curriculumId?: string;

  // Draft CLOs from AI
  draftCLOs?: Array<{
    id: string;
    text: string;
    bloomsLevel: string;
    bloomsVerb: string;
    ploMappings: string[];
    kscMappings: string[];
    confidence: number;
    reasoning?: string;
  }>;
}

export interface ProgramLite {
  id: string;
  code: string;
  nameTh: string;
  faculty: string;
  plos: Array<{ code: string; text: string; bloomsLevel: string }>;
}

export interface VerificationLite {
  id: string;
  status: "planned" | "in_progress" | "review" | "signed" | "closed";
  programId: string;
  courseId: string;
  semesterId: string;
  courseCode?: string;
  courseName?: string;
  committee: Array<{ uid: string; name: string; role: string }>;
  reviewerActions?: number;
  createdAt: Timestamp;
  scheduledDate?: Timestamp;
}

// ============ Read queries ============

export function useProgram(programId: string | null) {
  return useQuery({
    queryKey: ["program", programId],
    enabled: !!programId,
    queryFn: async (): Promise<ProgramLite | null> => {
      if (!programId) return null;
      const { db } = getFirebase();
      const snap = await getDoc(doc(db, "programs", programId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<ProgramLite, "id">) };
    },
  });
}

/**
 * คืน "ปีการศึกษาปัจจุบัน" แบบสั้น 2 หลัก (เช่น "68" สำหรับปีการศึกษา 2568)
 * Thai academic year: ก.ค.-มิ.ย.
 *   ก.ค. yyyy → มิ.ย. yyyy+1 = ปีการศึกษา yyyy (พ.ศ.)
 */
export function currentAcademicYearShort(): string {
  const now = new Date();
  const m = now.getMonth() + 1;     // 1-12
  const yyyy = now.getFullYear() + 543;  // CE → BE
  // ก.ค. (7) - ธ.ค. (12) = ปีการศึกษา yyyy
  // ม.ค. (1) - มิ.ย. (6) = ปีการศึกษา yyyy - 1
  const academicYear = m >= 7 ? yyyy : yyyy - 1;
  return String(academicYear).slice(-2); // "68"
}

/**
 * Check ว่า section นั้นอยู่ในปีการศึกษาที่กำหนด
 * semester format: "1/68" / "2/68" / "S/68"
 */
export function isInAcademicYear(semester: string | undefined, yearShort: string): boolean {
  if (!semester) return false;
  const parts = semester.split("/");
  if (parts.length !== 2) return false;
  return parts[1] === yearShort;
}

/**
 * Normalize a semester string to the canonical "N-YYYY" form used as the Firestore
 * subcollection doc id (e.g. `programs/{}/courses/{}/semesters/{semesterId}`).
 *
 *   "1/68"    → "1-2568"
 *   "2/68"    → "2-2568"
 *   "1/2568"  → "1-2568"
 *   "2-2568"  → "2-2568" (already normalized — pass through)
 *   "" / null → "" (caller decides fallback)
 *
 * The Buddhist Era year (4 digits) is reconstructed by prepending "25" to a
 * 2-digit short year. If we ever roll past 2600 BE, update this prefix.
 */
export function normalizeSemesterId(raw: string | undefined | null): string {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // Already canonical
  if (/^\d+-\d{4}$/.test(s)) return s;
  // "1/68" → "1-2568"
  const short = s.match(/^(\d+|S)\/(\d{2})$/i);
  if (short) return `${short[1]}-25${short[2]}`;
  // "1/2568" → "1-2568"
  const full = s.match(/^(\d+|S)\/(\d{4})$/i);
  if (full) return `${full[1]}-${full[2]}`;
  return s;
}

/**
 * Derive the canonical semesterId for a course: the semester in which the
 * course is actually taught. Used as the default when an instructor page
 * doesn't have `?sem=` in the URL.
 *
 * Priority:
 *   1) The instructor's own non-cancelled section (`section.semester`)
 *   2) The first non-cancelled section
 *   3) The first section regardless of cancelled state
 *   4) Empty string if no sections at all (caller falls back as needed)
 */
export function courseDefaultSemesterId(
  course: { sections?: Array<{ instructorUid?: string; semester?: string; cancelled?: boolean }> } | null | undefined,
  uid?: string | null,
  yearShort?: string | null,   // optional — จำกัดเฉพาะ section ในปีการศึกษานี้ (เช่น "69")
): string {
  const secs = course?.sections ?? [];
  if (secs.length === 0) return "";
  const inYear = (s: { semester?: string }) => !yearShort || isInAcademicYear(s.semester, yearShort);
  const candidate =
    (uid ? secs.find(s => s.instructorUid === uid && !s.cancelled && inYear(s)) : null) ??
    secs.find(s => !s.cancelled && inYear(s)) ??
    (uid ? secs.find(s => s.instructorUid === uid && !s.cancelled) : null) ??
    secs.find(s => !s.cancelled) ??
    secs[0];
  return normalizeSemesterId(candidate?.semester);
}

/**
 * Remove a user (typically themselves, but also usable by chair to remove any
 * instructor) from a course's sections. Filters out all sections where
 * `instructorUid === uid`, updates `allInstructorUids`, and if the removed
 * user was the legacy primary `instructorUid`, promotes the next remaining
 * instructor.
 *
 * Used by the "ไม่ได้สอน" button on the instructor course card — fixes wrong
 * Bulk Assign roster entries without needing chair intervention.
 */
export function useRemoveInstructorFromCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      uid: string;
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "courses", args.courseId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("ไม่พบรายวิชา");
      const data = snap.data() as {
        sections?: CourseSection[];
        allInstructorUids?: string[];
        instructorUid?: string;
        instructorName?: string;
      };
      const sections = data.sections ?? [];
      const remaining = sections.filter(s => s.instructorUid !== args.uid);
      if (remaining.length === sections.length) {
        throw new Error("ไม่พบ section ของคุณในรายวิชานี้");
      }
      const newAllUids = Array.from(new Set(remaining.map(s => s.instructorUid).filter(Boolean) as string[]));
      // Promote a new "primary" if the removed user was the listed one
      let primaryUid = data.instructorUid ?? "";
      let primaryName = data.instructorName ?? "";
      if (primaryUid === args.uid) {
        const next = remaining[0];
        primaryUid = next?.instructorUid ?? "";
        primaryName = next?.instructorName ?? "";
      }
      await updateDoc(ref, {
        sections: remaining,
        allInstructorUids: newAllUids,
        instructorUid: primaryUid,
        instructorName: primaryName,
      });
      return { removedSectionCount: sections.length - remaining.length };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["my-courses", vars.uid] });
      qc.invalidateQueries({ queryKey: ["program-courses", vars.programId] });
      qc.invalidateQueries({ queryKey: ["course", vars.programId, vars.courseId] });
    },
  });
}

/**
 * Review progress of one verification: how many DISTINCT committee members
 * have recorded at least one reviewer action (agree/comment on any dimension).
 *
 * Business rule (per chair): if ≥1 committee member has reviewed, the course
 * counts as "ทวนสอบแล้ว" (verified) — the rest of the committee adds weight
 * but isn't required for the verified status.
 */
export function useVerificationReviewProgress(programId: string, verifyId: string | null | undefined) {
  return useQuery({
    queryKey: ["verification-review-progress", programId, verifyId],
    enabled: !!programId && !!verifyId,
    queryFn: async (): Promise<{
      reviewerCount: number;
      reviewerNames: string[];
      hasReviewed: boolean;
    }> => {
      if (!verifyId) return { reviewerCount: 0, reviewerNames: [], hasReviewed: false };
      const { db } = getFirebase();
      const snap = await getDocs(
        collection(db, "programs", programId, "verifications", verifyId, "reviewerActions")
      );
      const names = new Map<string, string>();
      for (const d of snap.docs) {
        const data = d.data() as { reviewerUid?: string; reviewerName?: string };
        if (data.reviewerUid) {
          names.set(data.reviewerUid, data.reviewerName ?? names.get(data.reviewerUid) ?? "");
        }
      }
      return {
        reviewerCount: names.size,
        reviewerNames: Array.from(names.values()).filter(Boolean),
        hasReviewed: names.size > 0,
      };
    },
  });
}

/**
 * Replace the entire `sections` array of a course (chair-only). Also re-derives
 * `allInstructorUids` (for array-contains queries) and `instructorUid` /
 * `instructorName` (legacy primary fields). Used by the Course Offering Planner
 * so the chair can add/remove/edit sections per semester.
 */
export function useReplaceCourseSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      sections: CourseSection[];
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "courses", args.courseId);
      const allUids = Array.from(new Set(
        args.sections.map(s => s.instructorUid).filter((u): u is string => !!u)
      ));
      const primary = args.sections.find(s => !s.cancelled) ?? args.sections[0];
      await updateDoc(ref, {
        sections: args.sections,
        allInstructorUids: allUids,
        instructorUid: primary?.instructorUid ?? "",
        instructorName: primary?.instructorName ?? "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["my-courses"] });
      qc.invalidateQueries({ queryKey: ["program-courses", vars.programId] });
      qc.invalidateQueries({ queryKey: ["course", vars.programId, vars.courseId] });
    },
  });
}

/**
 * ยกเลิก/คืนสถานะ section (อาจารย์ผู้สอนกดยกเลิกเมื่อไม่มีนิสิตลงทะเบียน)
 */
export function useToggleCancelSection() {
  return useMutation({
    mutationFn: async (vars: {
      programId: string;
      courseId: string;
      sectionNumber: number;
      cancelled: boolean;
      reason?: string;
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", vars.programId, "courses", vars.courseId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("ไม่พบรายวิชา");
      const data = snap.data() as { sections?: CourseSection[] };
      const sections = data.sections ?? [];
      const idx = sections.findIndex((s) => s.section === vars.sectionNumber);
      if (idx === -1) throw new Error(`ไม่พบ section ${vars.sectionNumber}`);
      const updated = [...sections];
      updated[idx] = {
        ...updated[idx],
        cancelled: vars.cancelled,
        cancelledReason: vars.cancelled ? (vars.reason ?? "ไม่มีนิสิตลงทะเบียน") : undefined,
        cancelledAt: vars.cancelled ? new Date().toISOString() : undefined,
      };
      await updateDoc(ref, { sections: updated });
    },
  });
}

export function useMyCourses(uid: string | null, opts?: { onlyCurrentYear?: boolean }) {
  const onlyCurrentYear = opts?.onlyCurrentYear ?? true;  // default = filter ปีปัจจุบัน
  return useQuery({
    queryKey: ["my-courses", uid, onlyCurrentYear],
    enabled: !!uid,
    queryFn: async (): Promise<CourseLite[]> => {
      if (!uid) return [];
      const { db } = getFirebase();
      const currentYear = currentAcademicYearShort();

      // Query by allInstructorUids array-contains (covers all sections)
      const qAll = query(
        collection(db, "programs", "demo-cs", "courses"),
        where("allInstructorUids", "array-contains", uid),
        where("active", "==", true)
      );
      const snapAll = await getDocs(qAll);
      const courses = new Map<string, CourseLite>();
      for (const d of snapAll.docs) {
        courses.set(d.id, { id: d.id, ...(d.data() as Omit<CourseLite, "id">) });
      }

      // Fallback: also query by legacy instructorUid (for courses not yet migrated)
      const qLegacy = query(
        collection(db, "programs", "demo-cs", "courses"),
        where("instructorUid", "==", uid),
        where("active", "==", true)
      );
      const snapLegacy = await getDocs(qLegacy);
      for (const d of snapLegacy.docs) {
        if (!courses.has(d.id)) {
          courses.set(d.id, { id: d.id, ...(d.data() as Omit<CourseLite, "id">) });
        }
      }

      let result = Array.from(courses.values());

      // Filter: เฉพาะรายวิชาที่อาจารย์ uid สอนในปีการศึกษาปัจจุบัน
      if (onlyCurrentYear) {
        result = result.filter((c) => {
          const mySections = (c.sections ?? []).filter((s) => s.instructorUid === uid);
          if (mySections.length === 0) {
            // ไม่มี section ระบุ → assume ปัจจุบัน (fallback for legacy data)
            return true;
          }
          return mySections.some((s) => isInAcademicYear(s.semester, currentYear));
        });
      }

      return result;
    },
  });
}

export function useProgramCourses(programId: string | null) {
  return useQuery({
    queryKey: ["program-courses", programId],
    enabled: !!programId,
    queryFn: async (): Promise<CourseLite[]> => {
      if (!programId) return [];
      const { db } = getFirebase();
      const q = query(
        collection(db, "programs", programId, "courses"),
        where("active", "==", true),
        orderBy("code")
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CourseLite, "id">) }));
    },
  });
}

// ============ Program CLO Report (chair / committee dashboard) ============

export interface CloReportRow {
  courseId: string;
  code: string;
  nameTh: string;
  nameEn?: string;
  credit?: number;
  creditString?: string;
  ploResponsibility?: string[];
  /** Section count from course doc */
  sectionCount: number;
  /** Status from semester doc */
  status: string;
  tqf3Submitted: boolean;
  tqf5Submitted: boolean;
  /** CLOs from TQF3 */
  clos: Array<{
    id: string;
    text: string;
    ploMappings: string[];
    targetRate: number;
    passThreshold: number;
  }>;
  /** Achievement from TQF5 — keyed by cloId */
  achievementByCloId: Record<string, {
    totalStudents: number;
    passedStudents: number;
    passRate: number;
    targetMet: boolean;
  }>;
  /** Grade distribution from TQF5 */
  gradeDistribution?: Record<string, number>;
  totalStudents: number;
}

/** Fetch all courses + their TQF3/5 for one semester. Used for program-wide CLO report. */
export function useProgramCloReport(programId: string, semesterId: string) {
  return useQuery({
    queryKey: ["program-clo-report", programId, semesterId],
    enabled: !!programId && !!semesterId,
    queryFn: async (): Promise<CloReportRow[]> => {
      const { db } = getFirebase();
      // 1. Fetch all active courses
      const coursesSnap = await getDocs(
        query(
          collection(db, "programs", programId, "courses"),
          where("active", "==", true),
          orderBy("code")
        )
      );

      // 2. For each course, fetch the semester doc in parallel
      const promises = coursesSnap.docs.map(async (cDoc) => {
        const c = cDoc.data() as DocumentData;
        const sectionsArr = (c.sections as unknown[]) ?? [];
        const semSnap = await getDoc(doc(db, "programs", programId, "courses", cDoc.id, "semesters", semesterId));
        const semData = semSnap.exists() ? semSnap.data() as DocumentData : null;
        const tqf3 = semData?.tqf3 ?? {};
        const tqf5 = semData?.tqf5 ?? {};
        const status = (semData?.status ?? "draft") as string;
        const tqf3Submitted = ["tqf3_submitted", "in_progress", "tqf5_submitted", "verifying", "verified"].includes(status);
        const tqf5Submitted = ["tqf5_submitted", "verifying", "verified"].includes(status);

        const clos = ((tqf3.clos ?? []) as Array<Record<string, unknown>>).map((x) => ({
          id: String(x.id ?? ""),
          text: String(x.text ?? ""),
          ploMappings: Array.isArray(x.ploMappings) ? x.ploMappings.map(String) : [],
          targetRate: Number(x.targetAchievementRate) || 0.8,
          passThreshold: Number(x.passThreshold) || 0.6,
        }));

        const achievementByCloId: CloReportRow["achievementByCloId"] = {};
        for (const a of (tqf5.cloAchievement ?? []) as Array<Record<string, unknown>>) {
          const cid = String(a.cloId ?? "");
          if (!cid) continue;
          achievementByCloId[cid] = {
            totalStudents: Number(a.totalStudents) || 0,
            passedStudents: Number(a.passedStudents) || 0,
            passRate: Number(a.passRate) || 0,
            targetMet: a.targetMet === true,
          };
        }

        const gradeDistribution = (tqf5.gradeDistribution ?? {}) as Record<string, number>;
        const totalStudents = Object.values(gradeDistribution).reduce(
          (s: number, n) => s + (Number(n) || 0),
          0
        );

        const row: CloReportRow = {
          courseId: cDoc.id,
          code: String(c.code ?? ""),
          nameTh: String(c.nameTh ?? ""),
          nameEn: String(c.nameEn ?? ""),
          credit: Number(c.credit) || 0,
          creditString: String(c.creditString ?? ""),
          ploResponsibility: Array.isArray(c.ploResponsibility) ? (c.ploResponsibility as string[]) : [],
          sectionCount: sectionsArr.length,
          status,
          tqf3Submitted,
          tqf5Submitted,
          clos,
          achievementByCloId,
          gradeDistribution,
          totalStudents,
        };
        return row;
      });

      const rows = await Promise.all(promises);
      return rows;
    },
  });
}

// ============ Program PLO Achievement Report ============

export interface PloReportRow {
  plo: string;
  courseCodes: string[];     // รายวิชาที่ map กับ PLO นี้ (มีข้อมูลจริง)
  semesters: string[];       // ภาคที่มีข้อมูล
  closMeasured: number;      // จำนวน CLO (× ภาค) ที่วัดได้
  closMet: number;           // CLO ที่บรรลุเป้า
  studentsTotal: number;     // ผลรวมนิสิต (ถ่วงน้ำหนัก)
  studentsPassedSum: number; // ผลรวมนิสิตที่ผ่าน
  achievementPct: number;    // ร้อยละการบรรลุ (ถ่วงน้ำหนักนิสิต)
  cloMetRate: number;        // ร้อยละ CLO ที่บรรลุเป้า
}

/**
 * รวมผลการบรรลุ PLO ทั้งหลักสูตร — aggregate CLO achievement (จาก มคอ.5) ของ
 * ทุกรายวิชา ทุกภาค ตาม CLO→PLO mapping (จาก มคอ.3). ใช้ในหน้ารายงานผล PLO.
 *
 * achievementPct = Σ(นิสิตที่ผ่าน CLO ที่ map กับ PLO) / Σ(นิสิตทั้งหมด) × 100
 * → ตีความเป็น "ร้อยละการบรรลุเฉลี่ยถ่วงน้ำหนักด้วยจำนวนนิสิต" ของ PLO นั้น
 */
export function useProgramPloReport(programId: string | null) {
  return useQuery({
    queryKey: ["program-plo-report", programId],
    enabled: !!programId,
    queryFn: async (): Promise<PloReportRow[]> => {
      const { db } = getFirebase();
      const pid = programId!;
      const coursesSnap = await getDocs(
        query(collection(db, "programs", pid, "courses"), where("active", "==", true))
      );

      type Acc = { courses: Set<string>; sems: Set<string>; closMeasured: number; closMet: number; studentsTotal: number; studentsPassed: number };
      const acc = new Map<string, Acc>();
      const ensure = (plo: string): Acc => {
        if (!acc.has(plo)) acc.set(plo, { courses: new Set(), sems: new Set(), closMeasured: 0, closMet: 0, studentsTotal: 0, studentsPassed: 0 });
        return acc.get(plo)!;
      };

      await Promise.all(coursesSnap.docs.map(async (cDoc) => {
        const c = cDoc.data() as DocumentData;
        const code = String(c.code ?? cDoc.id);
        const semsSnap = await getDocs(collection(db, "programs", pid, "courses", cDoc.id, "semesters"));
        for (const semDoc of semsSnap.docs) {
          const semData = semDoc.data() as DocumentData;
          const tqf3 = (semData.tqf3 ?? {}) as DocumentData;
          const tqf5 = (semData.tqf5 ?? {}) as DocumentData;
          const ach = (tqf5.cloAchievement ?? []) as Array<Record<string, unknown>>;
          if (ach.length === 0) continue;
          const achById = new Map<string, { total: number; passed: number; targetMet: boolean }>();
          for (const a of ach) {
            const cid = String(a.cloId ?? "");
            if (!cid) continue;
            achById.set(cid, { total: Number(a.totalStudents) || 0, passed: Number(a.passedStudents) || 0, targetMet: a.targetMet === true });
          }
          for (const clo of (tqf3.clos ?? []) as Array<Record<string, unknown>>) {
            const cid = String(clo.id ?? "");
            const a = achById.get(cid);
            if (!a || a.total === 0) continue;
            const plos = Array.isArray(clo.ploMappings) ? clo.ploMappings.map(String) : [];
            for (const plo of plos) {
              const e = ensure(plo);
              e.courses.add(code);
              e.sems.add(semDoc.id);
              e.closMeasured++;
              if (a.targetMet) e.closMet++;
              e.studentsTotal += a.total;
              e.studentsPassed += a.passed;
            }
          }
        }
      }));

      return Array.from(acc.entries()).map(([plo, e]) => ({
        plo,
        courseCodes: [...e.courses].sort(),
        semesters: [...e.sems].sort(),
        closMeasured: e.closMeasured,
        closMet: e.closMet,
        studentsTotal: e.studentsTotal,
        studentsPassedSum: e.studentsPassed,
        achievementPct: e.studentsTotal > 0 ? (e.studentsPassed / e.studentsTotal) * 100 : 0,
        cloMetRate: e.closMeasured > 0 ? (e.closMet / e.closMeasured) * 100 : 0,
      })).sort((a, b) => a.plo.localeCompare(b.plo, undefined, { numeric: true }));
    },
  });
}

export function useCourse(programId: string, courseId: string | null) {
  return useQuery({
    queryKey: ["course", programId, courseId],
    enabled: !!courseId,
    queryFn: async (): Promise<CourseLite | null> => {
      if (!courseId) return null;
      const { db } = getFirebase();
      const snap = await getDoc(doc(db, "programs", programId, "courses", courseId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<CourseLite, "id">) };
    },
  });
}

export interface TQF5SectionResult {
  sectionNo: number;
  instructorUid: string;
  instructorName: string;
  cloAchievement: Array<{
    cloId: string; cloText?: string;
    totalStudents: number; passedStudents: number;
    passRate: number; targetMet: boolean;
  }>;
  gradeDistribution: {
    A: number; "B+": number; B: number; "C+": number;
    C: number; "D+": number; D: number; F: number;
    W?: number;
  };
  reflection: {
    strengths: string; weaknesses: string;
    improvementsForNextSemester: string;
  };
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: Timestamp | { seconds: number; nanoseconds: number };
}

export interface TQF5Data {
  status: string;
  cloAchievement: Array<{
    cloId: string;
    cloText?: string;
    totalStudents: number;
    passedStudents: number;
    passRate: number;
    targetMet: boolean;
  }>;
  gradeDistribution: {
    A: number; "B+": number; B: number; "C+": number;
    C: number; "D+": number; D: number; F: number;
    W?: number;
  };
  reflection: {
    strengths: string;
    weaknesses: string;
    improvementsForNextSemester: string;
  };
  outliers?: Array<{ type: string; description: string }>;
  /** Per-section results — used when course has multiple sections.
      If non-empty, the aggregate cloAchievement/gradeDistribution/reflection
      above are computed from these. */
  sectionResults?: TQF5SectionResult[];
  // Supplementary (MSU template, AI-drafted + user-edited)
  hoursComparison?: Array<{ topic: string; plannedHours: number; actualHours: number; deviationReason: string; fixMethod: string }>;
  methodEffectiveness?: Array<{ cloId: string; teachingMethod: string; teachingEffective: boolean; teachingIssue: string; assessmentMethod: string; assessmentEffective: boolean; assessmentIssue: string }>;
  improvementSuggestions?: string;
  gradeOutlierFactors?: string;
  assessmentDeviations?: Array<{ deviation: string; reason: string }>;
  resourceProblems?: Array<{ problem: string; impact: string }>;
  courseEvaluation?: Array<{ feedback: string; instructorResponse: string }>;
  prevImprovementProgress?: Array<{ previousPlan: string; progress: string }>;
  otherImprovements?: string;
  suggestionsToProgram?: string;
  nextImprovementPlan?: Array<{ proposal: string; deadline: string; owner: string }>;
  // Draft (from AI, not yet accepted)
  draftHoursComparison?: TQF5Data["hoursComparison"];
  draftMethodEffectiveness?: TQF5Data["methodEffectiveness"];
  draftImprovementSuggestions?: string;
  draftGradeOutlierFactors?: string;
  draftAssessmentDeviations?: TQF5Data["assessmentDeviations"];
  draftResourceProblems?: TQF5Data["resourceProblems"];
  draftCourseEvaluation?: TQF5Data["courseEvaluation"];
  draftPrevImprovementProgress?: TQF5Data["prevImprovementProgress"];
  draftOtherImprovements?: string;
  draftSuggestionsToProgram?: string;
  draftNextImprovementPlan?: TQF5Data["nextImprovementPlan"];
  draftGeneratedAt?: unknown;
  // AI-drafted reflection (per-field accept) — separate from supplementary draft
  draftReflection?: {
    strengths: string;
    weaknesses: string;
    improvementsForNextSemester: string;
  };
  draftReflectionGeneratedAt?: unknown;
  submittedAt?: Timestamp;
  // Author/submitter
  authorUid?: string;
  authorName?: string;
  submittedBy?: string;
  submittedByName?: string;
}

export function useTQF5(programId: string, courseId: string, semesterId: string) {
  return useQuery({
    queryKey: ["tqf5", programId, courseId, semesterId],
    queryFn: async (): Promise<TQF5Data | null> => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", programId, "courses", courseId, "semesters", semesterId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data() as DocumentData;
      const t = data?.tqf5;
      if (!t) return null;
      return {
        status: data?.status ?? "draft",
        cloAchievement: t.cloAchievement ?? [],
        gradeDistribution: t.gradeDistribution ?? { A: 0, "B+": 0, B: 0, "C+": 0, C: 0, "D+": 0, D: 0, F: 0 },
        reflection: t.reflection ?? { strengths: "", weaknesses: "", improvementsForNextSemester: "" },
        outliers: t.outliers ?? [],
        sectionResults: (t.sectionResults ?? []) as TQF5SectionResult[],
        // Accepted supplementary
        hoursComparison: t.hoursComparison ?? [],
        methodEffectiveness: t.methodEffectiveness ?? [],
        improvementSuggestions: t.improvementSuggestions ?? "",
        gradeOutlierFactors: t.gradeOutlierFactors ?? "",
        assessmentDeviations: t.assessmentDeviations ?? [],
        resourceProblems: t.resourceProblems ?? [],
        courseEvaluation: t.courseEvaluation ?? [],
        prevImprovementProgress: t.prevImprovementProgress ?? [],
        otherImprovements: t.otherImprovements ?? "",
        suggestionsToProgram: t.suggestionsToProgram ?? "",
        nextImprovementPlan: t.nextImprovementPlan ?? [],
        // Draft
        draftHoursComparison: t.draftHoursComparison ?? [],
        draftMethodEffectiveness: t.draftMethodEffectiveness ?? [],
        draftImprovementSuggestions: t.draftImprovementSuggestions ?? "",
        draftGradeOutlierFactors: t.draftGradeOutlierFactors ?? "",
        draftAssessmentDeviations: t.draftAssessmentDeviations ?? [],
        draftResourceProblems: t.draftResourceProblems ?? [],
        draftCourseEvaluation: t.draftCourseEvaluation ?? [],
        draftPrevImprovementProgress: t.draftPrevImprovementProgress ?? [],
        draftOtherImprovements: t.draftOtherImprovements ?? "",
        draftSuggestionsToProgram: t.draftSuggestionsToProgram ?? "",
        draftNextImprovementPlan: t.draftNextImprovementPlan ?? [],
        draftGeneratedAt: t.draftGeneratedAt,
        draftReflection: (t.draftReflection ?? { strengths: "", weaknesses: "", improvementsForNextSemester: "" }) as TQF5Data["draftReflection"],
        draftReflectionGeneratedAt: t.draftReflectionGeneratedAt,
        submittedAt: t.submittedAt,
        // Author/submitter
        authorUid: t.authorUid ?? "",
        authorName: t.authorName ?? "",
        submittedBy: t.submittedBy ?? "",
        submittedByName: t.submittedByName ?? "",
      };
    },
  });
}

/** Trigger AI to draft TQF5 supplementary content */
export function useGenerateTQF5Draft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; courseId: string; semesterId: string }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "generateTQF5Draft");
      const res = await fn(args);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

/** Trigger AI to draft TQF5 reflection (3 fields — fast + cheap) */
export function useGenerateReflectionDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; courseId: string; semesterId: string }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "generateReflectionDraft");
      const res = await fn(args);
      return res.data as { ok: boolean; draftReflection: { strengths: string; weaknesses: string; improvementsForNextSemester: string }; costUSD?: number };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

// ============ Import instructor's own มคอ.3/5 + AI writing review ============

export interface Tqf3ImportData {
  developmentObjectives?: string;
  clos?: Array<{ id?: string; text?: string; bloomsLevel?: string; ploMappings?: string[] }>;
  weeklyPlan?: Array<{ week?: number; topic?: string; subTopics?: string[]; lectureHours?: number; practiceHours?: number; activities?: string[]; media?: string[] }>;
  references?: string[];
}
export interface Tqf5ImportData {
  reflection?: { strengths?: string; weaknesses?: string; improvementsForNextSemester?: string };
  improvementSuggestions?: string;
  gradeOutlierFactors?: string;
  otherImprovements?: string;
  suggestionsToProgram?: string;
}

/** แปลงข้อความ มคอ.3/5 ที่อาจารย์เขียนเอง → โครงสร้างฟอร์ม (ให้ตรวจก่อนบันทึก) */
export function useImportTqfFromText() {
  return useMutation({
    mutationFn: async (args: { kind: "tqf3" | "tqf5"; text: string }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "importTqfFromText");
      const res = await fn(args);
      return res.data as { ok: boolean; kind: string; data: Tqf3ImportData | Tqf5ImportData };
    },
  });
}

/** AI อ่าน มคอ.3/5 ในระบบ → เสนอแนะการปรับปรุงการเขียน (ไม่แก้ให้) */
export function useReviewTqf() {
  return useMutation({
    mutationFn: async (args: { programId: string; courseId: string; semesterId: string; kind: "tqf3" | "tqf5" }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "reviewTqf");
      const res = await fn(args);
      return res.data as { ok: boolean; suggestions: string[] };
    },
  });
}

export function useSaveTQF5() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
      data: Omit<TQF5Data, "status" | "submittedAt">;
    }) => {
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      // Sanitize — strip undefined and coerce types so Firestore accepts them
      const d = args.data;
      const cleanArr = <T extends object>(arr: T[] | undefined, shape: (x: T) => T): T[] =>
        (arr ?? []).map(shape);
      const cleanGrade = (g: TQF5Data["gradeDistribution"] | undefined) => ({
        A: Number(g?.A) || 0, "B+": Number(g?.["B+"]) || 0, B: Number(g?.B) || 0,
        "C+": Number(g?.["C+"]) || 0, C: Number(g?.C) || 0,
        "D+": Number(g?.["D+"]) || 0, D: Number(g?.D) || 0, F: Number(g?.F) || 0,
        W: Number(g?.W) || 0,
      });
      const payload = {
        cloAchievement: cleanArr(d.cloAchievement, (a) => ({
          cloId: String(a.cloId ?? ""),
          cloText: String(a.cloText ?? ""),
          totalStudents: Number(a.totalStudents) || 0,
          passedStudents: Number(a.passedStudents) || 0,
          passRate: Number(a.passRate) || 0,
          targetMet: a.targetMet === true,
        })),
        gradeDistribution: cleanGrade(d.gradeDistribution),
        reflection: {
          strengths: String(d.reflection?.strengths ?? ""),
          weaknesses: String(d.reflection?.weaknesses ?? ""),
          improvementsForNextSemester: String(d.reflection?.improvementsForNextSemester ?? ""),
        },
        outliers: cleanArr(d.outliers, (o) => ({ type: String(o.type ?? ""), description: String(o.description ?? "") })),
        // Supplementary
        hoursComparison: cleanArr(d.hoursComparison, (r) => ({
          topic: String(r.topic ?? ""),
          plannedHours: Number(r.plannedHours) || 0,
          actualHours: Number(r.actualHours) || 0,
          deviationReason: String(r.deviationReason ?? ""),
          fixMethod: String(r.fixMethod ?? ""),
        })),
        methodEffectiveness: cleanArr(d.methodEffectiveness, (m) => ({
          cloId: String(m.cloId ?? ""),
          teachingMethod: String(m.teachingMethod ?? ""),
          teachingEffective: m.teachingEffective === true,
          teachingIssue: String(m.teachingIssue ?? ""),
          assessmentMethod: String(m.assessmentMethod ?? ""),
          assessmentEffective: m.assessmentEffective === true,
          assessmentIssue: String(m.assessmentIssue ?? ""),
        })),
        improvementSuggestions: String(d.improvementSuggestions ?? ""),
        gradeOutlierFactors: String(d.gradeOutlierFactors ?? ""),
        assessmentDeviations: cleanArr(d.assessmentDeviations, (x) => ({ deviation: String(x.deviation ?? ""), reason: String(x.reason ?? "") })),
        resourceProblems: cleanArr(d.resourceProblems, (x) => ({ problem: String(x.problem ?? ""), impact: String(x.impact ?? "") })),
        courseEvaluation: cleanArr(d.courseEvaluation, (x) => ({ feedback: String(x.feedback ?? ""), instructorResponse: String(x.instructorResponse ?? "") })),
        prevImprovementProgress: cleanArr(d.prevImprovementProgress, (x) => ({ previousPlan: String(x.previousPlan ?? ""), progress: String(x.progress ?? "") })),
        otherImprovements: String(d.otherImprovements ?? ""),
        suggestionsToProgram: String(d.suggestionsToProgram ?? ""),
        nextImprovementPlan: cleanArr(d.nextImprovementPlan, (x) => ({ proposal: String(x.proposal ?? ""), deadline: String(x.deadline ?? ""), owner: String(x.owner ?? "") })),
        sectionResults: (d.sectionResults ?? []).map((s) => ({
          sectionNo: Number(s.sectionNo) || 0,
          instructorUid: String(s.instructorUid ?? ""),
          instructorName: String(s.instructorName ?? ""),
          cloAchievement: cleanArr(s.cloAchievement, (a) => ({
            cloId: String(a.cloId ?? ""), cloText: String(a.cloText ?? ""),
            totalStudents: Number(a.totalStudents) || 0,
            passedStudents: Number(a.passedStudents) || 0,
            passRate: Number(a.passRate) || 0,
            targetMet: a.targetMet === true,
          })),
          gradeDistribution: cleanGrade(s.gradeDistribution),
          reflection: {
            strengths: String(s.reflection?.strengths ?? ""),
            weaknesses: String(s.reflection?.weaknesses ?? ""),
            improvementsForNextSemester: String(s.reflection?.improvementsForNextSemester ?? ""),
          },
          submittedBy: String(s.submittedBy ?? ""),
          submittedByName: String(s.submittedByName ?? ""),
        })),
        authorUid: u?.uid ?? "",
        authorName: u?.displayName ?? "",
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, { tqf5: payload, updatedAt: serverTimestamp() }, { merge: true });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

export function useSubmitTQF5() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
    }) => {
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await updateDoc(ref, {
        status: "tqf5_submitted",
        "tqf5.submittedAt": serverTimestamp(),
        "tqf5.submittedBy": u?.uid ?? "",
        "tqf5.submittedByName": u?.displayName ?? "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

export interface WeeklyPlanEntry {
  week: number;
  topic: string;
  subTopics: string[];
  lectureHours: number;
  practiceHours: number;
  activities: string[];
  media: string[];
  assessment?: string;
  cloIds: string[];
}

export interface AssessmentScheduleEntry {
  cloId: string;
  method: string;
  weeks: number[];
  weightPercent: number;
}

export interface CloTeachingMethod {
  cloId: string;
  teachingMethod: string;
  assessmentMethod: string;
}

export interface HoursPerSemester {
  lecture: number;
  practice: number;
  selfStudy: number;
}

export interface AssessmentBreakdownItem {
  method: string;
  weightPercent: number;
}

export interface Rubric {
  content: string;
  criteria: string;
}

/**
 * Migrate TQF3/TQF5 data from one semester subcollection doc to another, for
 * a single course. Used when an instructor mistakenly filled data at the
 * wrong semester (e.g. the global default "2-2568" instead of the course's
 * actual offered semester "1-2568"). The source doc is COPIED then DELETED.
 *
 * Safety: refuses to overwrite a destination that already has TQF3 CLOs or
 * TQF5 grade data — caller must resolve the conflict manually.
 */
export function useMigrateSemesterData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      fromSemesterId: string;
      toSemesterId: string;
    }) => {
      if (args.fromSemesterId === args.toSemesterId) {
        throw new Error("ภาคต้นทาง = ภาคปลายทาง — ไม่ต้องย้าย");
      }
      const { db } = getFirebase();
      const fromRef = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.fromSemesterId);
      const toRef = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.toSemesterId);
      const fromSnap = await getDoc(fromRef);
      if (!fromSnap.exists()) throw new Error(`ไม่พบ doc ที่ภาค ${args.fromSemesterId}`);
      const fromData = fromSnap.data() as Record<string, unknown>;
      // Check destination
      const toSnap = await getDoc(toRef);
      if (toSnap.exists()) {
        const toData = toSnap.data() as Record<string, unknown>;
        const t3 = (toData.tqf3 ?? {}) as { clos?: unknown[] };
        const t5 = (toData.tqf5 ?? {}) as { cloAchievement?: unknown[]; sectionResults?: unknown[] };
        const destHasData = (t3.clos?.length ?? 0) > 0
          || (t5.cloAchievement?.length ?? 0) > 0
          || (t5.sectionResults?.length ?? 0) > 0;
        if (destHasData) {
          throw new Error(
            `ภาคปลายทาง (${args.toSemesterId}) มีข้อมูลอยู่แล้ว — ` +
            `กรุณาตรวจสอบและรวมข้อมูลด้วยมือก่อน เพื่อไม่ให้เขียนทับโดยไม่ตั้งใจ`
          );
        }
      }
      // Copy source → destination (preserving all fields including tqf3/tqf5/status)
      await setDoc(toRef, fromData);
      // Delete source
      await deleteDoc(fromRef);
      return { fromSemesterId: args.fromSemesterId, toSemesterId: args.toSemesterId };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId] });
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId] });
      qc.invalidateQueries({ queryKey: ["course-semesters-summary", vars.programId, vars.courseId] });
    },
  });
}

/**
 * List ALL semesters (subcollection docs) for a course, with a quick summary
 * of what's filled in each. Used by the verification page so the committee can
 * pick which semester to audit — verification is not tied to "current semester."
 *
 * Returns an array sorted newest-first (by lexicographic semesterId, e.g.
 * "2-2568" > "1-2568" > "2-2567"…).
 */
export function useCourseSemestersSummary(programId: string, courseId: string) {
  return useQuery({
    queryKey: ["course-semesters-summary", programId, courseId],
    enabled: !!programId && !!courseId,
    queryFn: async (): Promise<Array<{
      semesterId: string;
      tqf3Status: string;
      tqf3HasContent: boolean;
      tqf3CloCount: number;
      tqf3SubmittedAt: unknown;
      tqf5Status: string;
      tqf5HasContent: boolean;
      tqf5SubmittedAt: unknown;
      tqf5SectionCount: number;
      tqf5TotalStudents: number;
    }>> => {
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, "programs", programId, "courses", courseId, "semesters"));
      const rows = snap.docs.map(d => {
        const data = d.data() as Record<string, unknown>;
        const t3 = (data.tqf3 ?? {}) as Record<string, unknown>;
        const t5 = (data.tqf5 ?? {}) as Record<string, unknown>;
        const t3clos = (t3.clos as unknown[]) ?? [];
        const t5cloAch = (t5.cloAchievement as unknown[]) ?? [];
        const t5sec = (t5.sectionResults as Array<{ gradeDistribution?: Record<string, number> }>) ?? [];
        const t5gd = (t5.gradeDistribution as Record<string, number>) ?? {};
        let t5students = Object.values(t5gd).reduce((s: number, n) => s + (Number(n) || 0), 0);
        if (t5students === 0 && t5sec.length > 0) {
          // Sum from sectionResults instead
          for (const s of t5sec) {
            const gd = (s.gradeDistribution ?? {}) as Record<string, number>;
            t5students += Object.values(gd).reduce((acc: number, n) => acc + (Number(n) || 0), 0);
          }
        }
        return {
          semesterId: d.id,
          tqf3Status: String(data.status ?? "draft"),
          tqf3HasContent: t3clos.length > 0,
          tqf3CloCount: t3clos.length,
          tqf3SubmittedAt: t3.submittedAt ?? null,
          tqf5Status: String(data.status ?? "draft"),
          tqf5HasContent: t5cloAch.length > 0 || t5sec.length > 0,
          tqf5SubmittedAt: t5.submittedAt ?? null,
          tqf5SectionCount: t5sec.length,
          tqf5TotalStudents: t5students,
        };
      });
      // Sort: prefer those with content, then newest semesterId first
      return rows.sort((a, b) => {
        const aHas = (a.tqf3HasContent || a.tqf5HasContent) ? 1 : 0;
        const bHas = (b.tqf3HasContent || b.tqf5HasContent) ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        // newest first: "2-2568" > "1-2568" > "2-2567" lexicographically
        return b.semesterId.localeCompare(a.semesterId);
      });
    },
  });
}

export function useTQF3(programId: string, courseId: string, semesterId: string) {
  return useQuery({
    queryKey: ["tqf3", programId, courseId, semesterId],
    queryFn: async (): Promise<{
      clos: CLO[];
      status: string;
      readinessScore: number;
      authorUid: string;
      authorName: string;
      submittedBy: string;
      submittedByName: string;
      // accepted/persisted
      weeklyPlan: WeeklyPlanEntry[];
      assessmentSchedule: AssessmentScheduleEntry[];
      cloTeachingMethods: CloTeachingMethod[];
      developmentObjectives: string;
      hoursPerSemester: HoursPerSemester;
      assessmentBreakdown: AssessmentBreakdownItem[];
      rubrics: Rubric[];
      references: string[];
      // draft from AI
      draftWeeklyPlan: WeeklyPlanEntry[];
      draftAssessmentSchedule: AssessmentScheduleEntry[];
      draftCloTeachingMethods: CloTeachingMethod[];
      draftDevelopmentObjectives: string;
      draftHoursPerSemester: HoursPerSemester;
      draftAssessmentBreakdown: AssessmentBreakdownItem[];
      draftRubrics: Rubric[];
      draftReferences: string[];
      draftWeeklyPlanGeneratedAt: unknown;
    } | null> => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", programId, "courses", courseId, "semesters", semesterId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data() as DocumentData;
      const t = data?.tqf3 ?? {};
      const emptyHours: HoursPerSemester = { lecture: 0, practice: 0, selfStudy: 0 };
      return {
        clos: (t.clos ?? []) as CLO[],
        status: (data?.status ?? "draft") as string,
        readinessScore: (t.readinessScore ?? 0) as number,
        authorUid: (t.authorUid ?? "") as string,
        authorName: (t.authorName ?? "") as string,
        submittedBy: (t.submittedBy ?? "") as string,
        submittedByName: (t.submittedByName ?? "") as string,
        // accepted
        weeklyPlan: (t.weeklyPlan ?? []) as WeeklyPlanEntry[],
        assessmentSchedule: (t.assessmentSchedule ?? []) as AssessmentScheduleEntry[],
        cloTeachingMethods: (t.cloTeachingMethods ?? []) as CloTeachingMethod[],
        developmentObjectives: (t.developmentObjectives ?? "") as string,
        hoursPerSemester: (t.hoursPerSemester ?? emptyHours) as HoursPerSemester,
        assessmentBreakdown: (t.assessmentBreakdown ?? []) as AssessmentBreakdownItem[],
        rubrics: (t.rubrics ?? []) as Rubric[],
        references: (t.references ?? []) as string[],
        // draft
        draftWeeklyPlan: (t.draftWeeklyPlan ?? []) as WeeklyPlanEntry[],
        draftAssessmentSchedule: (t.draftAssessmentSchedule ?? []) as AssessmentScheduleEntry[],
        draftCloTeachingMethods: (t.draftCloTeachingMethods ?? []) as CloTeachingMethod[],
        draftDevelopmentObjectives: (t.draftDevelopmentObjectives ?? "") as string,
        draftHoursPerSemester: (t.draftHoursPerSemester ?? emptyHours) as HoursPerSemester,
        draftAssessmentBreakdown: (t.draftAssessmentBreakdown ?? []) as AssessmentBreakdownItem[],
        draftRubrics: (t.draftRubrics ?? []) as Rubric[],
        draftReferences: (t.draftReferences ?? []) as string[],
        draftWeeklyPlanGeneratedAt: t.draftWeeklyPlanGeneratedAt,
      };
    },
  });
}

/** Trigger AI to draft 15-week teaching plan */
export function useGenerateWeeklyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
    }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "generateWeeklyPlan");
      const res = await fn(args);
      return res.data as {
        ok: boolean;
        weeks: WeeklyPlanEntry[];
        assessmentSchedule: AssessmentScheduleEntry[];
        costUSD: number;
        elapsedMs: number;
      };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

/** Save instructor-edited weekly plan + all TQF3 supplementary content */
export function useSaveWeeklyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
      weeklyPlan: WeeklyPlanEntry[];
      assessmentSchedule: AssessmentScheduleEntry[];
      cloTeachingMethods?: CloTeachingMethod[];
      developmentObjectives?: string;
      hoursPerSemester?: HoursPerSemester;
      assessmentBreakdown?: AssessmentBreakdownItem[];
      rubrics?: Rubric[];
      references?: string[];
    }) => {
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);

      // Sanitize: Firestore rejects undefined values. Coerce every field to a defined value.
      const cleanedPlan = args.weeklyPlan.map((w) => ({
        week: Number(w.week) || 0,
        topic: String(w.topic ?? ""),
        subTopics: Array.isArray(w.subTopics) ? w.subTopics.filter((x) => x != null).map(String) : [],
        lectureHours: Number(w.lectureHours) || 0,
        practiceHours: Number(w.practiceHours) || 0,
        activities: Array.isArray(w.activities) ? w.activities.filter((x) => x != null).map(String) : [],
        media: Array.isArray(w.media) ? w.media.filter((x) => x != null).map(String) : [],
        assessment: String(w.assessment ?? ""),
        cloIds: Array.isArray(w.cloIds) ? w.cloIds.filter((x) => x != null).map(String) : [],
      }));

      const cleanedSched = args.assessmentSchedule.map((a) => ({
        cloId: String(a.cloId ?? ""),
        method: String(a.method ?? ""),
        weeks: Array.isArray(a.weeks) ? a.weeks.map((n) => Number(n) || 0) : [],
        weightPercent: Number(a.weightPercent) || 0,
      }));

      const cleanedCloMethods = (args.cloTeachingMethods ?? []).map((m) => ({
        cloId: String(m.cloId ?? ""),
        teachingMethod: String(m.teachingMethod ?? ""),
        assessmentMethod: String(m.assessmentMethod ?? ""),
      }));

      const cleanedHours = {
        lecture: Number(args.hoursPerSemester?.lecture) || 0,
        practice: Number(args.hoursPerSemester?.practice) || 0,
        selfStudy: Number(args.hoursPerSemester?.selfStudy) || 0,
      };

      const cleanedBreakdown = (args.assessmentBreakdown ?? []).map((b) => ({
        method: String(b.method ?? ""),
        weightPercent: Number(b.weightPercent) || 0,
      }));

      const cleanedRubrics = (args.rubrics ?? []).map((r) => ({
        content: String(r.content ?? ""),
        criteria: String(r.criteria ?? ""),
      }));

      const cleanedRefs = (args.references ?? []).filter((x) => x != null).map(String);

      await updateDoc(ref, {
        "tqf3.weeklyPlan": cleanedPlan,
        "tqf3.assessmentSchedule": cleanedSched,
        "tqf3.cloTeachingMethods": cleanedCloMethods,
        "tqf3.developmentObjectives": String(args.developmentObjectives ?? ""),
        "tqf3.hoursPerSemester": cleanedHours,
        "tqf3.assessmentBreakdown": cleanedBreakdown,
        "tqf3.rubrics": cleanedRubrics,
        "tqf3.references": cleanedRefs,
        "tqf3.weeklyPlanUpdatedAt": serverTimestamp(),
        "tqf3.authorUid": u?.uid ?? "",
        "tqf3.authorName": u?.displayName ?? "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

export function useAssignedVerifications(uid: string | null) {
  return useQuery({
    queryKey: ["assigned-verifications", uid],
    enabled: !!uid,
    queryFn: async (): Promise<VerificationLite[]> => {
      if (!uid) return [];
      const { db } = getFirebase();
      // Use collection group query — need composite index
      // For pilot: just query the demo program
      const q = query(
        collection(db, "programs", "demo-cs", "verifications"),
      );
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<VerificationLite, "id">) }));
      return all.filter(v => v.committee?.some(m => m.uid === uid));
    },
  });
}

/**
 * All verifications in a program (not filtered by committee). Used by the
 * instructor view to detect whether a scheduled course has actually been
 * verified — matching by normalized courseCode — even if the verification
 * plan's `verificationId`/status hasn't been synced back.
 */
export function useProgramVerifications(programId: string | null) {
  return useQuery({
    queryKey: ["program-verifications", programId],
    enabled: !!programId,
    queryFn: async (): Promise<Array<VerificationLite & {
      signatures?: Array<{ uid: string; name: string; role: string }>;
    }>> => {
      if (!programId) return [];
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, "programs", programId, "verifications"));
      return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<VerificationLite, "id">) }));
    },
  });
}

export function useVerification(programId: string, verifyId: string) {
  return useQuery({
    queryKey: ["verification", programId, verifyId],
    queryFn: async () => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", programId, "verifications", verifyId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as DocumentData) };
    },
  });
}

export function useReviewerActions(programId: string, verifyId: string) {
  return useQuery({
    queryKey: ["reviewer-actions", programId, verifyId],
    queryFn: async () => {
      const { db } = getFirebase();
      const q = collection(db, "programs", programId, "verifications", verifyId, "reviewerActions");
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },
  });
}

// ============ Users (for instructor assignment) ============

export interface UserLite {
  uid: string;
  email: string;
  displayName: string;
  roles: string[];
  external?: boolean;       // true = ผู้ทรงคุณวุฒิภายนอกภาควิชา
  position?: string;        // เช่น "รศ.ดร.", "ผศ.ดร."
  affiliation?: string;     // เช่น "มหาวิทยาลัยขอนแก่น"
  expertise?: string;       // ความเชี่ยวชาญ
  placeholder?: boolean;    // true ถ้ายังไม่ได้ login จริง
}

/** Add an external committee reviewer (not yet logged in — placeholder user). */
export function useAddExternalReviewer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      displayName: string;
      position?: string;
      affiliation?: string;
      email?: string;
      expertise?: string;
    }) => {
      const { db } = getFirebase();
      // Generate a slug-like UID
      const slug = "ext-" + args.displayName
        .toLowerCase()
        .replace(/[\s.()]+/g, "-")
        .replace(/[^\w\-ก-๙]/g, "")
        .slice(0, 40) + "-" + Math.random().toString(36).slice(2, 6);
      const ref = doc(db, "users", slug);
      await setDoc(ref, {
        email: args.email ?? "",
        displayName: args.displayName,
        roles: ["external", "committee"],
        external: true,
        position: args.position ?? "",
        affiliation: args.affiliation ?? "",
        expertise: args.expertise ?? "",
        placeholder: true,
        programs: ["demo-cs"],
        createdAt: serverTimestamp(),
      });
      return { uid: slug };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users-list"] });
    },
  });
}

/** Delete an external committee placeholder. */
export function useRemoveExternalReviewer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { uid: string }) => {
      const { db } = getFirebase();
      const { deleteDoc } = await import("firebase/firestore");
      await deleteDoc(doc(db, "users", args.uid));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users-list"] });
    },
  });
}

/** All users in system. For pilot use — small user base. */
export function useUsersList() {
  return useQuery({
    queryKey: ["users-list"],
    queryFn: async (): Promise<UserLite[]> => {
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, "users"));
      return snap.docs.map(d => ({
        uid: d.id,
        ...(d.data() as Omit<UserLite, "uid">),
      }));
    },
  });
}

/** Generate AI Pre-Analysis for a verification (5 dimensions). */
export function useGeneratePreAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; verificationId: string }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "generatePreAnalysis");
      const result = await fn(args);
      return result.data as {
        ok: boolean;
        dimensions: Array<{
          id: string;
          name: string;
          criterion: string;
          score: number;
          status: "pass" | "warning" | "fail";
          findings: Array<{ severity: string; text: string }>;
          rating: number;
        }>;
        overallScore: number;
        costUSD: number;
        elapsedMs: number;
      };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["verification", vars.programId, vars.verificationId] });
    },
  });
}

/** Create a new verification round. */
export function useCreateVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      courseCode: string;
      courseName: string;
      semesterId: string;
      scheduledDate: Date;
      committee: Array<{ uid: string; name: string; role: "chair" | "internal" | "external" | "secretary" }>;
      createdByUid: string;
    }) => {
      const { db } = getFirebase();
      const verificationsRef = collection(db, "programs", args.programId, "verifications");
      const newDoc = await addDoc(verificationsRef, {
        programId: args.programId,
        courseId: args.courseId,
        courseCode: args.courseCode,
        courseName: args.courseName,
        semesterId: args.semesterId,
        status: "in_progress",
        scheduledDate: Timestamp.fromDate(args.scheduledDate),
        committee: args.committee.map(m => ({
          ...m,
          invitedAt: Timestamp.now(),
        })),
        signatures: [],
        createdAt: serverTimestamp(),
        createdBy: args.createdByUid,
      });

      // Fire-and-forget: trigger AI Pre-Analysis
      // Don't await — let user navigate to verification page; analysis runs in background
      (async () => {
        try {
          const { app } = getFirebase();
          const { getFunctions, httpsCallable } = await import("firebase/functions");
          const functions = getFunctions(app, "asia-east1");
          const fn = httpsCallable(functions, "generatePreAnalysis");
          await fn({ programId: args.programId, verificationId: newDoc.id });
        } catch (e) {
          console.warn("Auto Pre-Analysis failed:", e);
        }
      })();

      return { verificationId: newDoc.id };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["assigned-verifications"] });
      qc.invalidateQueries({ queryKey: ["verification-plan", vars.programId, 2568] });
    },
  });
}

/** Delete a verification (for cleaning demo data). */
export function useDeleteVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; verifyId: string }) => {
      const { db } = getFirebase();
      await import("firebase/firestore").then(async ({ deleteDoc }) => {
        await deleteDoc(doc(db, "programs", args.programId, "verifications", args.verifyId));
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assigned-verifications"] });
    },
  });
}

/** Assign instructor to a course. */
export function useAssignInstructor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      instructorUid: string;
      instructorName: string;
    }) => {
      const { db } = getFirebase();
      const courseRef = doc(db, "programs", args.programId, "courses", args.courseId);
      await updateDoc(courseRef, {
        instructorUid: args.instructorUid,
        instructorName: args.instructorName,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["program-courses", vars.programId] });
      qc.invalidateQueries({ queryKey: ["my-courses"] });
    },
  });
}

// ============ Evidence Files ============

export const EVIDENCE_CATEGORIES = [
  { id: "tqf3-5", label: "เอกสาร มคอ.3 และ มคอ.5" },
  { id: "syllabus", label: "Course Syllabus" },
  { id: "grades", label: "เอกสารใบส่งเกรด" },
  { id: "evaluation", label: "ผลการประเมินโดยนิสิต" },
  { id: "exams", label: "ข้อสอบกลางภาค + ปลายภาค พร้อมเฉลย" },
  { id: "materials", label: "ตัวอย่างเอกสารประกอบการสอน" },
  { id: "samples", label: "ตัวอย่างผลงานนิสิต" },
  { id: "other", label: "อื่นๆ" },
] as const;

export type EvidenceCategoryId = typeof EVIDENCE_CATEGORIES[number]["id"];

export interface EvidenceFile {
  id: string;
  category: EvidenceCategoryId;
  filename: string;
  storagePath: string;
  downloadUrl: string;
  sizeBytes: number;
  contentType: string;
  uploadedByUid: string;
  uploadedByName: string;
  uploadedAt: Timestamp | { seconds: number; nanoseconds: number };
}

/** Read the array of evidence files saved on the semester doc. */
export function useEvidenceFiles(programId: string, courseId: string, semesterId: string) {
  return useQuery({
    queryKey: ["evidence-files", programId, courseId, semesterId],
    queryFn: async (): Promise<EvidenceFile[]> => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", programId, "courses", courseId, "semesters", semesterId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return [];
      const data = snap.data() as DocumentData;
      return (data?.evidence ?? []) as EvidenceFile[];
    },
  });
}

/** Upload a file to Firebase Storage + append metadata to the semester doc's evidence array. */
export function useUploadEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
      file: File;
      category: EvidenceCategoryId;
    }) => {
      const { db, auth, storage } = getFirebase();
      const u = auth.currentUser;
      if (!u) throw new Error("not authenticated");
      const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const { arrayUnion } = await import("firebase/firestore");

      const safeName = args.file.name.replace(/[/\\?%*:|"<>]/g, "-");
      const ts = Date.now();
      const storagePath = `programs/${args.programId}/courses/${args.courseId}/${args.semesterId}/evidence/${args.category}/${ts}-${safeName}`;
      const sref = storageRef(storage, storagePath);
      await uploadBytes(sref, args.file, { contentType: args.file.type });
      const downloadUrl = await getDownloadURL(sref);

      const fileMeta: EvidenceFile = {
        id: `${ts}-${Math.random().toString(36).slice(2, 8)}`,
        category: args.category,
        filename: args.file.name,
        storagePath,
        downloadUrl,
        sizeBytes: args.file.size,
        contentType: args.file.type || "application/octet-stream",
        uploadedByUid: u.uid,
        uploadedByName: u.displayName ?? "",
        uploadedAt: Timestamp.now(),
      };

      const semRef = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await setDoc(semRef, { evidence: arrayUnion(fileMeta), updatedAt: serverTimestamp() }, { merge: true });
      return fileMeta;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["evidence-files", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

/** Delete an evidence file from Storage and remove from the array. */
export function useDeleteEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      courseId: string;
      semesterId: string;
      file: EvidenceFile;
    }) => {
      const { db, storage } = getFirebase();
      const { ref: storageRef, deleteObject } = await import("firebase/storage");
      const { arrayRemove } = await import("firebase/firestore");
      try {
        await deleteObject(storageRef(storage, args.file.storagePath));
      } catch (e) {
        console.warn("storage delete failed (file may already be gone):", e);
      }
      const semRef = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await setDoc(semRef, { evidence: arrayRemove(args.file), updatedAt: serverTimestamp() }, { merge: true });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["evidence-files", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

// ============ Mutations ============

export function useSaveCLOs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string; courseId: string; semesterId: string; clos: CLO[];
    }) => {
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await setDoc(ref, {
        status: "draft",
        tqf3: {
          clos: args.clos,
          readinessScore: computeReadinessScore(args.clos),
          authorUid: u?.uid ?? "",
          authorName: u?.displayName ?? "",
          updatedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

export function useSubmitTQF3() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string; courseId: string; semesterId: string;
    }) => {
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await updateDoc(ref, {
        status: "tqf3_submitted",
        "tqf3.submittedAt": serverTimestamp(),
        "tqf3.submittedBy": u?.uid ?? "",
        "tqf3.submittedByName": u?.displayName ?? "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

/**
 * Withdraw a previously-submitted TQF3 — moves status back to "draft" so the
 * instructor can edit and re-submit. ONLY allowed if status === "tqf3_submitted"
 * (NOT if verification has started — status "verifying", "verified", "in_progress",
 * "tqf5_submitted"). The submittedAt/By metadata is preserved as `previousSubmittedAt`
 * etc. so we have an audit trail.
 */
export function useWithdrawTQF3() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string; courseId: string; semesterId: string;
      currentStatus: string;
      reason: string;
    }) => {
      // Guard: only permit withdraw at "tqf3_submitted" state
      if (args.currentStatus !== "tqf3_submitted") {
        throw new Error(
          `ไม่อนุญาตให้ถอน มคอ.3 ในสถานะ "${args.currentStatus}" — ` +
          `ถ้าจะแก้ไขต้องประสานกับประธานหลักสูตรก่อน เพราะการทวนสอบเริ่มดำเนินการแล้ว`
        );
      }
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await updateDoc(ref, {
        status: "draft",
        "tqf3.previousSubmittedAt": new Date(), // snapshot before withdraw
        "tqf3.withdrawnAt": serverTimestamp(),
        "tqf3.withdrawnBy": u?.uid ?? "",
        "tqf3.withdrawnByName": u?.displayName ?? "",
        "tqf3.withdrawReason": args.reason,
        "tqf3.submittedAt": null,
        "tqf3.submittedBy": "",
        "tqf3.submittedByName": "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

/**
 * Withdraw a previously-submitted TQF5 — moves status back to "tqf3_submitted"
 * (TQF3 is still considered submitted; only TQF5 is reopened). ONLY allowed if
 * status === "tqf5_submitted" (NOT if verification has started).
 */
export function useWithdrawTQF5() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string; courseId: string; semesterId: string;
      currentStatus: string;
      reason: string;
    }) => {
      if (args.currentStatus !== "tqf5_submitted") {
        throw new Error(
          `ไม่อนุญาตให้ถอน มคอ.5 ในสถานะ "${args.currentStatus}" — ` +
          `ถ้าจะแก้ไขต้องประสานกับประธานหลักสูตรก่อน เพราะการทวนสอบเริ่มดำเนินการแล้ว`
        );
      }
      const { db, auth } = getFirebase();
      const u = auth.currentUser;
      const ref = doc(db, "programs", args.programId, "courses", args.courseId, "semesters", args.semesterId);
      await updateDoc(ref, {
        status: "tqf3_submitted",
        "tqf5.previousSubmittedAt": new Date(),
        "tqf5.withdrawnAt": serverTimestamp(),
        "tqf5.withdrawnBy": u?.uid ?? "",
        "tqf5.withdrawnByName": u?.displayName ?? "",
        "tqf5.withdrawReason": args.reason,
        "tqf5.submittedAt": null,
        "tqf5.submittedBy": "",
        "tqf5.submittedByName": "",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["tqf5", vars.programId, vars.courseId, vars.semesterId] });
      qc.invalidateQueries({ queryKey: ["tqf3", vars.programId, vars.courseId, vars.semesterId] });
    },
  });
}

export function useReviewerAct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      verifyId: string;
      reviewerUid: string;
      reviewerName: string;
      dimension: string;
      action: "agree" | "disagree" | "comment";
      comment?: string;
      rating?: number;
    }) => {
      const { db } = getFirebase();
      const ref = collection(db, "programs", args.programId, "verifications", args.verifyId, "reviewerActions");
      await addDoc(ref, {
        reviewerUid: args.reviewerUid,
        reviewerName: args.reviewerName,
        dimension: args.dimension,
        action: args.action,
        comment: args.comment ?? "",
        rating: args.rating ?? null,
        createdAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["reviewer-actions", vars.programId, vars.verifyId] });
    },
  });
}

export function useSignVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string; verifyId: string;
      signerUid: string; signerName: string; signerRole: string;
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "verifications", args.verifyId);
      const snap = await getDoc(ref);
      const existing = (snap.data()?.signatures ?? []) as Array<{ uid: string }>;
      const newSig = {
        uid: args.signerUid,
        name: args.signerName,
        role: args.signerRole,
        signedAt: Timestamp.now(),
      };
      await updateDoc(ref, {
        signatures: [...existing.filter(s => s.uid !== args.signerUid), newSig],
        status: "signed",
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["verification", vars.programId, vars.verifyId] });
    },
  });
}

// ============ Helpers ============

function computeReadinessScore(clos: CLO[]): number {
  if (clos.length === 0) return 0;
  let score = 0;
  let total = 0;
  for (const clo of clos) {
    total += 4;
    if (clo.text && clo.text.length > 10) score++;
    if (clo.bloomsLevel) score++;
    if (clo.bloomsVerb) score++;
    if (clo.ploMappings && clo.ploMappings.length > 0) score++;
  }
  return Math.round((score / total) * 100);
}
